import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { RootState } from '@center-pwa/store/store';

type InvoiceStatus = 'paid' | 'due' | 'overdue';

const MOCK_PLAN = {
  daysRemaining: 12,
  seats: { used: 4, total: 10 },
};

const MOCK_INVOICES: Array<{
  id: string;
  date: string;
  amountCents: number;
  status: InvoiceStatus;
}> = [
  { id: 'inv-001', date: '2026-04-01', amountCents: 0, status: 'paid' },
  { id: 'inv-002', date: '2026-03-01', amountCents: 0, status: 'paid' },
  { id: 'inv-003', date: '2026-02-01', amountCents: 0, status: 'paid' },
];

const BillingMockSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language]
  );
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }),
    [i18n.language]
  );

  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);
  const orgId = useMemo(() => {
    if (!identity?.users || !userId) return undefined;
    const currentUser = identity.users.find((u: any) => u.id === userId);
    if (!currentUser) return undefined;
    const org = Array.isArray(currentUser.organization)
      ? currentUser.organization[0]
      : currentUser.organization;
    return (org as any)?.id as string | undefined;
  }, [identity, userId]);

  useEffect(() => {
    if (!orgId) return;
    logAnalyticsEvent('org_settings_section_viewed', undefined, {
      sectionId: 'billing',
      orgId,
      isMocked: 'true',
    });
    logAnalyticsEvent('org_settings_mocks_section_clicked', undefined, {
      sectionId: 'billing',
      orgId,
    });
  }, [orgId, logAnalyticsEvent]);

  return (
    <div className="org-settings-section">
      <div className="org-settings-section__demo-banner" role="status" aria-live="polite">
        <i className="pi pi-info-circle" aria-hidden="true" />
        <span>{t('orgSettings.demoBanner')}</span>
      </div>

      <header className="org-settings-section__header">
        <h2 className="org-settings-section__title">{t('orgSettings.sections.billing')}</h2>
      </header>

      <div className="org-settings-section__plan-card">
        <div className="org-settings-section__plan-name">{t('orgSettings.billing.planName')}</div>
        <div className="org-settings-section__plan-status">
          {t('orgSettings.billing.daysRemaining', { count: MOCK_PLAN.daysRemaining })}
        </div>
        <div className="org-settings-section__plan-seats">
          {t('orgSettings.billing.seatsUsed', {
            used: MOCK_PLAN.seats.used,
            total: MOCK_PLAN.seats.total,
          })}
        </div>
      </div>

      <section className="org-settings-section__subsection">
        <h3 className="org-settings-section__subtitle">
          {t('orgSettings.billing.invoicesTitle')}
        </h3>
        <table
          className="org-settings-section__table"
          aria-label={t('orgSettings.billing.invoicesTitle')}
        >
          <thead>
            <tr>
              <th scope="col">{t('orgSettings.billing.invoiceDate')}</th>
              <th scope="col">{t('orgSettings.billing.invoiceAmount')}</th>
              <th scope="col">{t('orgSettings.billing.invoiceStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_INVOICES.map((inv) => (
              <tr key={inv.id}>
                <td>{dateFormatter.format(new Date(inv.date))}</td>
                <td>{currencyFormatter.format(inv.amountCents / 100)}</td>
                <td>{t(`orgSettings.billing.invoiceStatusValue.${inv.status}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default BillingMockSection;
